import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Volume2, VolumeX, ArrowUp, Globe } from "lucide-react";
import { useNavigate } from "react-router-dom";
import FloatingPillNav from "./FloatingPillNav";
import CustomCursor from "./CustomCursor";
import { useCart } from "../contexts/CartContext";
import { useLanguage } from "../contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ImmersiveLayout — shared dark-immersive wrapper used by all redesigned pages.
 * Provides: dark theme, custom cursor, floating pill nav, audio toggle, brand wordmark,
 * scroll-to-top button.
 */
export default function ImmersiveLayout({ children, showWordmark = true, showAudio = true, settings: passedSettings }) {
  const navigate = useNavigate();
  const { cart } = useCart();
  const { language, toggleLang } = useLanguage();
  const cartCount = cart?.items?.length || 0;
  const [settings, setSettings] = useState(passedSettings || null);
  const [audioOn, setAudioOn] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    if (passedSettings) return;
    axios.get(`${API}/cms/site-settings`).then(r => setSettings(r.data)).catch(() => {});
  }, [passedSettings]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 320);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const toggleAudio = () => {
    if (!audioRef.current) return;
    if (audioOn) {
      audioRef.current.pause();
      setAudioOn(false);
    } else {
      audioRef.current.play().catch(() => {});
      setAudioOn(true);
    }
  };

  return (
    <div className="dark-immersive min-h-screen relative" data-testid="immersive-layout">
      <CustomCursor />

      {settings?.audio_url && (
        <audio ref={audioRef} src={settings.audio_url} loop preload="none" />
      )}

      {showWordmark && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="fixed top-5 left-5 lg:top-7 lg:left-10 z-50"
          onClick={() => navigate("/")}
          role="link"
          data-testid="immersive-wordmark"
          data-magnet
          style={{ cursor: "none" }}
        >
          <span className="font-heading text-2xl tracking-tight text-seikat-bone">
            Seikat<span className="text-seikat-pink">.</span>
          </span>
        </motion.div>
      )}

      {/* Prominent Language Toggle (top-right) */}
      <motion.button
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
        onClick={toggleLang}
        aria-label="Toggle language"
        data-testid="immersive-lang-toggle"
        data-magnet
        className="fixed top-4 right-5 lg:top-6 lg:right-10 z-50 flex items-center gap-2 px-4 py-2 rounded-full glass-dark text-seikat-bone hover:text-seikat-pink transition-colors group"
      >
        <Globe size={13} strokeWidth={1.6} className="group-hover:rotate-12 transition-transform duration-500" />
        <span className="font-mono text-[11px] tracking-[0.3em] uppercase">
          <span className={language === "id" ? "text-seikat-pink" : "text-seikat-bone-muted"}>ID</span>
          <span className="mx-1 text-seikat-bone-muted/40">/</span>
          <span className={language === "en" ? "text-seikat-pink" : "text-seikat-bone-muted"}>EN</span>
        </span>
      </motion.button>

      {/* Audio toggle */}
      {showAudio && settings?.audio_url && (
        <button
          onClick={toggleAudio}
          aria-label="Toggle ambient audio"
          data-testid="immersive-audio-toggle"
          data-magnet
          className="fixed bottom-24 right-5 lg:bottom-6 lg:right-6 z-50 w-11 h-11 rounded-full glass-dark flex items-center justify-center text-seikat-bone hover:text-seikat-pink transition-colors"
        >
          {audioOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
        </button>
      )}

      {/* Scroll to top */}
      {scrolled && (
        <motion.button
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label="Scroll to top"
          data-testid="immersive-scroll-top"
          data-magnet
          className="fixed bottom-40 right-5 lg:bottom-20 lg:right-6 z-50 w-11 h-11 rounded-full glass-dark flex items-center justify-center text-seikat-bone hover:text-seikat-pink transition-colors"
        >
          <ArrowUp size={16} />
        </motion.button>
      )}

      <main className="relative">{children}</main>

      <FloatingPillNav cartCount={cartCount} />
    </div>
  );
}
